import { fetchSampleRequestBarcodesData } from '../../Service/ApiService';
import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { showAlert } from 'utils/ErrorMessage';

const BarcodeScanController = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const user = JSON.parse(localStorage.getItem('user'));
  const addMoreDraft = location.state?.addMoreDraft || null;

  const [barcodeText, setBarcodeText] = React.useState('');
  const [barcodeError, setBarcodError] = React.useState('');
  const [barcodeData, setBarcodeData] = React.useState([]);

  const parseBarcodes = (value) => {
    let trimmed = (value || '').trim();
    if (trimmed.endsWith(',')) {
      trimmed = trimmed.slice(0, -1);
    }

    return trimmed
      .split(',')
      .map((b) => b.trim())
      .filter(Boolean);
  };

  const handleBarcodesChange = (e) => {
    let inputValue = e.target.value;

    if (inputValue.endsWith('\n')) {
      inputValue = inputValue.trim();
      if (inputValue !== '') {
        inputValue += ',';
      }
      processBarcodes(inputValue);
      e.target.value = '';
      return;
    }

    setBarcodeText(inputValue);
  };

  const handleBarcodesPaste = (e) => {
    e.preventDefault();

    const pastedText = e.clipboardData.getData('text');
    const formatted = pastedText
      .trim()
      .replace(/\s+/g, ',')
      .replace(/,+/g, ',');

    const combined = barcodeText ? `${barcodeText},${formatted}` : formatted;

    setBarcodeText(combined);
    processBarcodes(combined);
  };

  const processBarcodes = (inputValue) => {
    setBarcodeText(inputValue);
    const barcodes = parseBarcodes(inputValue);

    if (barcodes.length > 0) {
      setBarcodError('');
      getData(`${barcodes.join(',')},`);
    }
  };

  const handleOkButtonClick = () => {
    if (barcodeText === '') {
      setBarcodError('Please enter barcode');
    } else {
      getData(barcodeText);
    }
  };

  const handleClearButtonClick = () => {
    setBarcodeText('');
    setBarcodError('');
    setBarcodeData([]);
  };

  const handleChange = (e) => {
    setBarcodeData([]);
    let inputValue = e.target.value;

    if (inputValue.endsWith('\n')) {
      inputValue = inputValue.trim();
      if (inputValue !== '') {
        inputValue += ',';
      }
      getData(inputValue);
      e.target.value = '';
    }

    inputValue = inputValue.trim();
    setBarcodeText(inputValue);
  };

  const getData = async (inputValue) => {
    try {
      const res = await fetchSampleRequestBarcodesData(
        user.userId,
        '',
        user.userCode,
        user.userVerticalId,
        inputValue,
        ''
      );

      if (res && res.success == 'true') {
        const materialList = res.materialList || [];
        const list = materialList.filter((item) => {
          const hasBarcode = String(item?.barcode ?? '').trim() !== '';
          const hasMaterialCode = String(item?.materialCode ?? '').trim() !== '';

          if (!hasBarcode && !hasMaterialCode) {
            showAlert('Invalid barcode entered.');
            return false;
          }

          return true;
        });
        const scanOrder = new Map(
          parseBarcodes(inputValue).map((barcode, index) => [String(barcode), index])
        );
        const latestFirstList = [...list].sort((a, b) => {
          const aIndex = scanOrder.get(String(a.barcode)) ?? -1;
          const bIndex = scanOrder.get(String(b.barcode)) ?? -1;
          return bIndex - aIndex;
        });

        setBarcodeData(latestFirstList);
        setBarcodError('');
        return latestFirstList;
      }

      const message = res?.msg || 'No Data Found!';
      setBarcodeData([]);
      setBarcodError(message);
      showAlert(message);
      return [];
    } catch (err) {
      console.error('User Sales Reports load error:', err);
      return [];
    }
  };

  const handleTextareaResize = (event) => {
    const textarea = event.target;
    textarea.style.height = 'auto';
  };

  const handleRemoveBarcode = (barcode) => {
    const updatedData = barcodeData.filter((item) => item.barcode !== barcode);
    const parts = barcodeText
      .split(',')
      .map((p) => p.trim())
      .filter((p) => p.length > 0 && p !== barcode);

    setBarcodeText(parts.join(','));
    setBarcodeData(updatedData);
  };

  const handleProceed = async () => {
    let currentData = barcodeData;

    if (currentData.length === 0) {
      const enteredBarcodes = parseBarcodes(barcodeText);
      if (enteredBarcodes.length > 0) {
        currentData = await getData(`${enteredBarcodes.join(',')},`);
      }
    }

    if (!currentData || currentData.length === 0) {
      const message = barcodeText ? (barcodeError || 'No Data Found!') : 'Please enter barcode';
      setBarcodError(message);
      showAlert(message);
      return;
    }

    setBarcodError('');
    if (addMoreDraft) {
      const existingData = Array.isArray(addMoreDraft.barcodeData) ? addMoreDraft.barcodeData : [];
      const existingBarcodes = new Set(
        existingData.map((item) => String(item?.barcode || '').trim()).filter(Boolean)
      );
      const existingMaterialKeys = new Set(
        existingData.map((item) =>
          [item?.materialID, item?.materialCode, item?.sizex, item?.shade, item?.stockType]
            .map((value) => String(value || '').trim())
            .join('|')
        )
      );
      const newMaterials = currentData.filter((item) => {
        const barcode = String(item?.barcode || '').trim();
        if (barcode) return !existingBarcodes.has(barcode);

        const materialKey = [item?.materialID, item?.materialCode, item?.sizex, item?.shade, item?.stockType]
          .map((value) => String(value || '').trim())
          .join('|');
        return !existingMaterialKeys.has(materialKey);
      });

      navigate('/sample-request-selection', {
        replace: true,
        state: {
          barcodeData: [...newMaterials, ...existingData],
          selectedCustomer: addMoreDraft.selectedCustomer || null,
          sampleCategory: addMoreDraft.sampleCategory || null,
          isReplication: Boolean(addMoreDraft.isReplication),
          isEditMode: Boolean(addMoreDraft.isEditMode),
          sampleRequestReplicateRefNo: addMoreDraft.sampleRequestReplicateRefNo || '',
          sampleRequestOrderId: addMoreDraft.sampleRequestOrderId || '',
          sampleRequestDocNo: addMoreDraft.sampleRequestDocNo || '',
          remark: addMoreDraft.remark || '',
          hasUnsavedChanges: true,
          returnToSampleRequestList: true
        }
      });
      return;
    }

    navigate('/sample-request-selection', { state: { barcodeData: currentData } });
  };

  return {
    barcodeText,
    barcodeData,
    barcodeError,
    handleOkButtonClick,
    handleClearButtonClick,
    handleChange,
    handleTextareaResize,
    handleRemoveBarcode,
    handleProceed,
    handleBarcodesChange,
    handleBarcodesPaste
  };
};

export default BarcodeScanController;
